import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
// DODANO: Import App z Capacitora do obsługi przycisku wstecz
import { App } from '@capacitor/app';
import { RenderMarkdownWithLatex } from "../../utils/utils";
import { useAndroidSafeArea } from "../../hooks/useAndroidSafeArea";
import { getSheetBackdropMotion, getSheetPanelMotion, useSheetSlideIn } from "../../utils/sheetMotion";
import { useActiveDomain, DOMAIN_LABELS } from "../../utils/domain";
import orzelLogo from "../../img/orzel_logo_192.png";

const AiChatPopup = ({
  isOpen,
  onClose,
  chatTitle,
  chatHistory,
  onSendMessage,
  isResponding,
}) => {
  const [userMessage, setUserMessage] = useState("");
  // Ten sam active_domain co reszta appki (Nav, Dashboard, Settings) — czytany
  // synchronicznie z localStorage, więc bez migania złym poziomem przy otwarciu.
  const activeDomain = useActiveDomain();
  const domainSubtitle = `${DOMAIN_LABELS[activeDomain]?.full || DOMAIN_LABELS.PP.full} z matematyki`;
  const chatContainerRef = useRef(null);

  const prefersReducedMotion = useReducedMotion();
  const slideIn = useSheetSlideIn(isOpen);

  const { paddingTop, paddingBottom } = useAndroidSafeArea();

  // --- KALKULACJA WYSOKOŚCI NAGŁÓWKA ---
  const headerHeight = `calc(4.2rem + ${paddingTop})`;

  const footerPadding = `calc(${paddingBottom} + 10px)`;

  // --- NOWE: PRZECHWYTYWANIE PRZYCISKU WSTECZ (ANDROID) ---
  useEffect(() => {
    // Jeśli modal nie jest otwarty, nie interesuje nas przycisk wstecz
    if (!isOpen) return;

    let backListener;

    const setupBackListener = async () => {
      // Rejestrujemy listenera, który nadpisuje domyślne zachowanie (czyli wyjście z appki/history back)
      backListener = await App.addListener('backButton', (data) => {
        // MĄDRA STRATEGIA: Zamiast blokować, po prostu zamykamy modal.
        // Jeśli chcesz być brutalny i zablokować, usuń linię poniżej.
        onClose(); 
      });
    };

    setupBackListener();

    // Cleanup: usuwamy listenera jak modal znika, żeby przywrócić normalne działanie wstecz
    return () => {
      if (backListener) {
        backListener.remove();
      }
    };
  }, [isOpen, onClose]);

  // --- BLOKOWANIE SCROLLOWANIA TŁA ---
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  // --- LOGIKA PRZEWIJANIA ---
  useEffect(() => {
    if (!chatContainerRef.current || chatHistory.length === 0) return;

    const container = chatContainerRef.current;
    const lastMessage = chatHistory[chatHistory.length - 1];

    setTimeout(() => {
      if (lastMessage.role === "model") {
        const messageElements = container.querySelectorAll('[data-message-role]');
        const lastMessageElement = messageElements[messageElements.length - 1];
        if (lastMessageElement) {
          lastMessageElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }, 100); 
  }, [chatHistory, isOpen]);

  // --- LOGIKA SYMULOWANIA MYŚLENIA ---
  const typingMessages = [
    "Analizuję treść zadania...",
    "Szukam wierszy w Karcie Wzorów...",
    "Sprawdzam obliczenia...",
    "Formułuję wyjaśnienie...",
    "Jeszcze chwilka..."
  ];
  const [typingIndex, setTypingIndex] = useState(0);

  useEffect(() => {
    if (!isResponding) return;
    const id = setInterval(
      () => setTypingIndex((i) => (i + 1) % typingMessages.length),
      2500
    );
    return () => clearInterval(id);
  }, [isResponding]);

  const handleAttemptSendMessage = () => {
    if (!userMessage.trim() || isResponding) return;
    onSendMessage(userMessage);
    setUserMessage("");
  };
  
  const handleSubmitForm = (e) => {
    e.preventDefault();
    handleAttemptSendMessage();
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* TŁO */}
          <motion.div
            key="ai-chat-backdrop"
            {...getSheetBackdropMotion()}
            className="fixed inset-0 z-[9998] bg-[#233855]/80 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* --- GŁÓWNY KONTENER (sheet wjeżdżający od dołu) --- */}
          <motion.div
            key="ai-chat-panel"
            {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
            className="fixed inset-x-0 bottom-0 z-[9999] bg-gray-50 w-full h-[100dvh] sm:h-[80vh] sm:max-w-2xl sm:mx-auto sm:bottom-[10vh] rounded-none sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
        
        {/* --- NAGŁÓWEK --- */}
        <div
            className="bg-white border-b border-gray-100 px-4 sm:px-6 pb-3 sm:py-4 flex justify-between items-end shadow-sm z-10 shrink-0"
            style={{
                    paddingTop: paddingTop,
                    height: headerHeight
                }}
        >
          <div className="flex items-center space-x-3">
            <div className="relative">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#233855] flex items-center justify-center overflow-hidden">
                    <img
                    src={orzelLogo}
                    alt="Orzeł AI"
                    className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 border-2 border-white rounded-full"></div>
            </div>

            <div>
                <h2 className="text-[#233855] font-apotek text-base sm:text-lg tracking-wider uppercase leading-none">
                {chatTitle || "Orzeł AI"}
                </h2>
                <p className="text-[10px] sm:text-xs text-gray-500 font-light mt-0.5">
                    {domainSubtitle}
                </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-[#233855] hover:bg-gray-100 p-3 -mr-2 rounded-full transition-all cursor-pointer z-50"
            aria-label="Zamknij"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* --- OBSZAR WIADOMOŚCI --- */}
        <div ref={chatContainerRef} className="flex-grow p-4 sm:p-6 overflow-y-auto space-y-6 bg-[#F3F4F6]">
            
          {chatHistory.length > 0 && (
             <div className="flex justify-center mb-4 sm:mb-6">
                <span className="text-[10px] sm:text-xs text-gray-400 font-medium bg-gray-200 px-3 py-1 rounded-full">
                    Początek rozmowy
                </span>
             </div>
          )}

          {chatHistory.slice(1).map((msg, index) => {
            const isUser = msg.role === "user";
            return (
                <div
                key={index}
                data-message-role={msg.role}
                className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}
                >
                <div
                    className={`max-w-[90%] p-3 sm:p-4 text-sm leading-relaxed shadow-sm relative break-words ${
                    isUser
                        ? "bg-[#233855] text-white rounded-2xl rounded-tr-none" 
                        : "bg-white text-gray-800 border border-gray-200 rounded-2xl rounded-tl-none"
                    }`}
                >
                    <RenderMarkdownWithLatex text={msg.parts[0]?.text || ""} />
                </div>
                </div>
            );
          })}

          {/* --- LOADING INDICATOR --- */}
          {isResponding && (
            <div className="flex justify-start">
              <div className="max-w-[90%] bg-white border border-gray-200 p-3 sm:p-4 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-2 sm:gap-3">
                <div className="flex space-x-1">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#E09F2E] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#E09F2E] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#E09F2E] rounded-full animate-bounce"></div>
                </div>
                <span className="text-xs text-gray-500 italic font-medium animate-pulse">
                  {typingMessages[typingIndex]}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* --- INPUT AREA --- */}
        <form
          onSubmit={handleSubmitForm}
          className="p-3 sm:p-4 bg-white border-t border-gray-200 shrink-0"
          style={{
            paddingBottom: footerPadding
          }}
        >
          <div className="relative flex items-end gap-2 bg-gray-50 p-2 rounded-xl border border-gray-300 focus-within:border-[#E09F2E] focus-within:ring-1 focus-within:ring-[#E09F2E] transition-all shadow-sm">
            <textarea
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !isResponding) {
                  e.preventDefault();
                  handleSubmitForm(e);
                }
              }}
              placeholder={
                isResponding
                  ? "Orzeł myśli..."
                  : "Dopytaj o szczegóły..."
              }
              className="flex-grow bg-transparent border-none focus:ring-0 p-2 text-sm text-gray-800 placeholder-gray-400 resize-none max-h-32 min-h-[44px]"
              rows={1}
              style={{ minHeight: '44px' }}
              disabled={isResponding}
            />
            
            <button
              type="submit"
              disabled={isResponding || !userMessage.trim()}
              className={`
                p-2.5 rounded-lg flex-shrink-0 transition-all duration-200
                ${isResponding || !userMessage.trim() 
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed" 
                  : "bg-[#E09F2E] text-white hover:bg-yellow-600 shadow-md transform active:scale-95"}
              `}
            >
               <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
               </svg>
            </button>
          </div>
          <div className="text-center mt-2 pb-1 sm:pb-0">
            <p className="text-[10px] text-gray-400">
                Orzeł AI może popełniać błędy. Sprawdzaj ważne informacje.
            </p>
          </div>
        </form>

          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AiChatPopup;